import { useState } from "react";
import WelcomeScreen from "@/components/screens/WelcomeScreen";
import InitialInfoScreen from "@/components/screens/InitialInfoScreen";
import QuestionScreen from "@/components/screens/QuestionScreen";
import IntroScreen from "@/components/screens/IntroScreen";
import ContactScreen from "@/components/screens/ContactScreen";
import ThankYouScreen from "@/components/screens/ThankYouScreen";
import ProgressBar from "@/components/ProgressBar";
import { formQuestions } from "@/data/questions";

import { useLocation } from "react-router-dom";

const ConversationalForm = () => {
  const location = useLocation();
  // Si viene desde landing (skipWelcome=true), empieza en step 1, sino en step 0
  const initialStep = location.state?.skipWelcome ? 1 : 0;
  
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Total steps: welcome (0) + questions (1-17) + contact (18) + thank you (19)
  const totalQuestions = formQuestions.length;
  const totalSteps = totalQuestions + 2; // +1 for contact screen, +1 for thank you

  const handleAnswer = (questionId, answer) => {
    setFormData(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleInitialInfo = (initialData) => {
    setFormData(prev => ({
      ...prev,
      ...initialData
    }));
    handleNext();
  };

  const handleNext = () => {
    setCurrentStep(prev => prev + 1);
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(1, prev - 1)); // No ir más atrás de la primera pregunta
  };

  const handleSubmit = (contactData) => {
    // ===========================================
    // OPTION MAPS - For mapping values to labels
    // ===========================================
    
    // Initial Info Screen options
    const tiposNegocioMap = {
      "agencia": "Agencia / Marketing",
      "consultoria": "Consultoría",
      "infoproductos": "Infoproductos / Formación",
      "otro": "Otro"
    };
    
    const tamanosEquipoMap = {
      "independiente": "Profesional independiente",
      "2-5": "2–5 personas",
      "6-10": "6–10 personas",
      "11-25": "11–25 personas",
      "25+": "+25 personas"
    };
    
    // Questions options
    const rolNegocioMap = {
      "fundador": "Fundador / CEO",
      "socio": "Socio",
      "director_marketing": "Director de marketing",
      "director_operaciones": "Director de operaciones",
      "gerente": "Gerente o Manager"
    };
    
    const facturacionMap = {
      "<5k": "< 5.000 €",
      "5k-10k": "5.000 – 10.000 €",
      "10k-30k": "10.000 – 30.000 €",
      "30k-100k": "30.000 – 100.000 €",
      "100k+": "+100.000 €"
    };
    
    const metricasMap = {
      "ticket_medio": "Ticket medio",
      "cac": "CAC (Costo de Adquisición de Clientes)",
      "ltv": "LTV (Valor del Ciclo de Vida del Cliente)",
      "tasa_conversion": "Tasa de conversión",
      "margen_venta": "Margen por venta",
      "ninguna": "Ninguna con claridad"
    };
    
    const escalabilidadMap = {
      "sin_problema": "Sí, sin problema",
      "con_estres": "Sí, pero con estrés",
      "colapsaria": "No, colapsaríamos"
    };
    
    const dependenciaMap = {
      "normalidad": "Funcionaría con normalidad",
      "pequenos_problemas": "Habría pequeños problemas",
      "ralentizaria": "Se ralentizaría mucho",
      "detendria": "Prácticamente se detendría"
    };
    
    const captacionMap = {
      "publicidad_redes": "Publicidad en redes (Google Ads, Meta Ads, etc)",
      "contenido_organico": "Contenido orgánico",
      "referidos": "Referidos",
      "captacion_directa": "Captación directa (mailing, llamadas, fuerza comercial, etc)",
      "no_clara": "No tengo una fuente clara"
    };
    
    const problemaCaptacionMap = {
      "baja_calidad": "Leads de baja calidad",
      "coste_elevado": "Coste elevado",
      "bajo_volumen": "Bajo volumen de leads",
      "baja_conversion": "Baja conversión a venta"
    };
    
    const puntoRupturaMap = {
      "captacion": "Captación/Marketing",
      "ventas": "Ventas",
      "operaciones": "Operaciones",
      "atencion_cliente": "Atención al cliente",
      "mi_tiempo": "Mi tiempo / yo mismo"
    };
    
    const gestionVentasMap = {
      "depende_mi": "Depende casi totalmente de mí y no hay un proceso claro",
      "basico_no_documentado": "Hay un proceso básico, pero no está documentado ni medido",
      "crm_simple": "Proceso básico con CRM simple, pero poco optimizado",
      "crm_pipeline": "Proceso claro con CRM, pipeline y algunas automatizaciones",
      "optimizado": "Proceso predecible y optimizado de forma continua"
    };
    
    const mayorCosteMap = {
      "resolver_problemas_fuegos": "Resolver problemas – Apagar fuegos",
      "tomar_decisiones": "Tomar decisiones",
      "coordinar": "Coordinar personas",
      "repetir_tareas": "Repetir tareas"
    };
    
    const expectativasIAMap = {
      "ahorrar_tiempo": "Ahorrar tiempo",
      "sustituir_repetitivas": "Sustituir tareas repetitivas",
      "ayudar_decidir": "Ayudarme a decidir mejor",
      "escalar_ventas": "Escalar ventas",
      "no_claro": "No lo tengo claro"
    };
    
    const prioridadMap = {
      "alta": "Alta: necesito claridad ya",
      "media": "Media: quiero explorarlo",
      "baja": "Baja: solo estoy mirando"
    };
    
    // ===========================================
    // HELPER FUNCTIONS
    // ===========================================
    
    // For single choice questions: { value, text }
    const buildSingleResponse = (value, optionsMap) => {
      if (!value) return null;
      return {
        value: value,
        text: optionsMap[value] || value
      };
    };
    
    // For multiple choice questions: { values, texts }
    const buildMultipleResponse = (values, optionsMap) => {
      if (!values || !Array.isArray(values) || values.length === 0) return null;
      return {
        values: values,
        texts: values.map(v => optionsMap[v] || v)
      };
    };
    
    // For text/textarea questions: { text }
    const buildTextResponse = (text) => {
      if (!text || (typeof text === 'string' && text.trim() === '')) return null;
      return { text: text };
    };
    
    // For multi_text questions (array of texts): { texts }
    const buildMultiTextResponse = (texts) => {
      if (!texts || !Array.isArray(texts)) return null;
      const filtered = texts.filter(t => t && t.trim() !== '');
      if (filtered.length === 0) return null;
      return { texts: filtered };
    };
    
    // ===========================================
    // METRICS CALCULATION
    // ===========================================
    
    const ALL_METRICS = ["ticket_medio", "cac", "ltv", "tasa_conversion", "margen_venta"];
    const userMetricsSelection = formData.metricas_control || [];
    const hasSelectedNone = userMetricsSelection.includes("ninguna");
    
    const controlledValues = hasSelectedNone ? [] : userMetricsSelection.filter(m => ALL_METRICS.includes(m));
    const notControlledValues = hasSelectedNone ? [...ALL_METRICS] : ALL_METRICS.filter(m => !userMetricsSelection.includes(m));
    
    const metricsPayload = {
      selection_type: hasSelectedNone ? "none" : "some",
      controlled: controlledValues.length > 0 ? {
        values: controlledValues,
        texts: controlledValues.map(v => metricasMap[v])
      } : null,
      not_controlled: notControlledValues.length > 0 ? {
        values: notControlledValues,
        texts: notControlledValues.map(v => metricasMap[v])
      } : null
    };
    
    // Clean up null values from metrics
    if (!metricsPayload.controlled) delete metricsPayload.controlled;
    if (!metricsPayload.not_controlled) delete metricsPayload.not_controlled;
    
    // ===========================================
    // BUILD PAYLOAD
    // ===========================================
    
    const payload = {
      contexto: {},
      claridad: {},
      crecimiento: {},
      operativa: {},
      ia: {},
      contacto: {
        nombre: contactData.nombre,
        email: contactData.email,
        telefono: contactData.telefono
      }
    };
    
    // CONTEXTO
    if (formData.nombre_empresa) payload.contexto.empresa = formData.nombre_empresa;
    if (formData.tipo_negocio) {
      payload.contexto.tipo = buildSingleResponse(formData.tipo_negocio, tiposNegocioMap);
    }
    if (formData.tipo_negocio_detalle) {
      payload.contexto.tipo_detalle = buildTextResponse(formData.tipo_negocio_detalle);
    }
    if (formData.tamano_equipo) payload.contexto.equipo = formData.tamano_equipo;
    
    const rolResponse = buildSingleResponse(formData.rol_negocio, rolNegocioMap);
    if (rolResponse) payload.contexto.rol = rolResponse;
    
    const facturacionResponse = buildSingleResponse(formData.facturacion, facturacionMap);
    if (facturacionResponse) payload.contexto.facturacion = facturacionResponse;
    
    // CLARIDAD
    payload.claridad.metrics = metricsPayload;
    
    const escalabilidadResponse = buildSingleResponse(formData.escalabilidad, escalabilidadMap);
    if (escalabilidadResponse) payload.claridad.escalabilidad = escalabilidadResponse;
    
    const dependenciaResponse = buildSingleResponse(formData.dependencia_operativa, dependenciaMap);
    if (dependenciaResponse) payload.claridad.dependencia = dependenciaResponse;
    
    // CRECIMIENTO
    const captacionResponse = buildSingleResponse(formData.fuentes_captacion, captacionMap);
    if (captacionResponse) payload.crecimiento.captacion = captacionResponse;
    
    const problemaResponse = buildSingleResponse(formData.problema_captacion, problemaCaptacionMap);
    if (problemaResponse) payload.crecimiento.problema = problemaResponse;
    
    const rupturaResponse = buildSingleResponse(formData.punto_ruptura, puntoRupturaMap);
    if (rupturaResponse) payload.crecimiento.punto_ruptura = rupturaResponse;
    
    // OPERATIVA
    const ventasResponse = buildSingleResponse(formData.gestion_ventas, gestionVentasMap);
    if (ventasResponse) payload.operativa.ventas = ventasResponse;
    
    const procesosResponse = buildMultiTextResponse(formData.procesos_complejos);
    if (procesosResponse) payload.operativa.procesos_complejos = procesosResponse;
    
    const costeResponse = buildSingleResponse(formData.mayor_coste, mayorCosteMap);
    if (costeResponse) payload.operativa.consumo_energia = costeResponse;
    
    // IA
    const expectativasResponse = buildMultipleResponse(formData.expectativas_ia, expectativasIAMap);
    if (expectativasResponse) payload.ia.expectativas = expectativasResponse;
    
    const prioridadResponse = buildSingleResponse(formData.nivel_prioridad, prioridadMap);
    if (prioridadResponse) payload.ia.prioridad = prioridadResponse;
    
    // PROBLEMA CLAVE
    const problemaClaveResponse = buildTextResponse(formData.problema_90_dias);
    if (problemaClaveResponse) payload.problema_clave = problemaClaveResponse;
    
    // Clean up empty objects
    if (Object.keys(payload.contexto).length === 0) delete payload.contexto;
    if (Object.keys(payload.claridad).length === 0) delete payload.claridad;
    if (Object.keys(payload.crecimiento).length === 0) delete payload.crecimiento;
    if (Object.keys(payload.operativa).length === 0) delete payload.operativa;
    if (Object.keys(payload.ia).length === 0) delete payload.ia;

    // Log payload for debugging
    console.log('Payload enviado al webhook:', JSON.stringify(payload, null, 2));

    // Fire-and-forget: Navigate immediately, send webhook in background
    handleNext(); // Move to thank you screen instantly
    
    // Send webhook asynchronously without blocking navigation
    fetch('https://n8n.davidautomatiza.com/webhook/auditoria-ia', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    })
    .then(response => {
      if (response.ok) {
        console.log('Form submitted successfully');
      } else {
        console.error('Form submission failed:', response.status);
      }
    })
    .catch(error => {
      console.error('Error submitting form:', error);
    });
  };

  const currentQuestion = currentStep > 1 && currentStep <= totalQuestions + 1
    ? formQuestions[currentStep - 2]
    : null;

  const progressPercentage = currentStep === 0 ? 0 : ((currentStep - 1) / totalSteps) * 100;

  return (
    <div className="min-h-screen bg-background">
      {/* Progress Bar - hidden on welcome and thank you screens */}
      {currentStep > 0 && currentStep <= totalSteps + 1 && (
        <ProgressBar progress={progressPercentage} />
      )}

      <div className="container max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Welcome Screen */}
        {currentStep === 0 && (
          <WelcomeScreen onStart={handleNext} />
        )}

        {/* Initial Info Screen - Step 1 */}
        {currentStep === 1 && (
          <InitialInfoScreen
            onSubmit={handleInitialInfo}
            onBack={currentStep > 1 ? handleBack : null}
            initialData={{
              nombre_empresa: formData.nombre_empresa,
              tipo_negocio: formData.tipo_negocio,
              tipo_negocio_detalle: formData.tipo_negocio_detalle,
              tamano_equipo: formData.tamano_equipo
            }}
          />
        )}

        {/* Question Screens or Intro Screens */}
        {currentQuestion && currentStep > 1 && (
          <>
            {currentQuestion.type === "intro" ? (
              <IntroScreen
                intro={currentQuestion}
                onNext={handleNext}
                onBack={currentStep > 1 ? handleBack : null}
              />
            ) : (
              <QuestionScreen
                question={{
                  ...currentQuestion,
                  onBack: currentStep > 1 ? handleBack : null
                }}
                answer={formData[currentQuestion.id]}
                onAnswer={handleAnswer}
                onNext={handleNext}
                formData={formData}
              />
            )}
          </>
        )}

        {/* Contact Screen */}
        {currentStep === totalQuestions + 2 && (
          <ContactScreen onSubmit={handleSubmit} isSubmitting={isSubmitting} />
        )}

        {/* Thank You Screen */}
        {currentStep === totalQuestions + 3 && (
          <ThankYouScreen />
        )}
      </div>
    </div>
  );
};

export default ConversationalForm;